import React, { FC, useEffect, useState } from 'react'
import { createUseStyles } from 'react-jss'
import { useAtom } from 'jotai'
import useSortMembersList from '../../../hooks/useSortMembersList'
import breakpoints from '../../../theme/breakpoints'
import { colors } from '../../../theme/palette'
import { baseButton, sortPlaceholder } from '../../../theme/reuseables'
import { CardHeader, CardHeaderCell, CardList } from '../../ui/Card'
import { SortButton } from '../../ui/SortButton'
import { Typography } from '../../ui/Typography/Typography'
import {
    allUpdatesDone,
    allUpdatesInProgress,
    updatePageDoneAtom,
    updatePageInProgressAtom,
    allUpdates,
    updatePageAtom,
} from '../../../store/filter/filterAtom'
import { L, L_HTML } from '../../providers/Lokalise'
import { getAllUpdates } from '../../../api/updates'
import { mapUpdates } from '../../../models/UpdateBatch'
import { UpdateDetails } from '../../modules/Updates/UpdateDetails'
import { HeaderLinks } from '../../modules/Headerlinks'
import { Spinner } from '../../ui/Loaders/Spinner'

const useStyles = createUseStyles({
    '@keyframes fadeIn': {
        from: {
            opacity: 0,
        },
        to: {
            opacity: 1,
        },
    },
    container: {
        padding: '0 1.5rem',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                padding: '0',
            },
        },
    },
    headerArea: {
        display: 'flex',
        padding: '.5rem 0',
        minHeight: '5rem',
    },
    headerAreaTop: {
        display: 'flex',
        padding: '1rem 0 .5rem',
        borderBottom: `1px solid ${colors.borderColor}`,
        marginBottom: '1rem',
    },
    topArea: {
        borderBottom: `1px solid ${colors.gray700}`,
        // TODO: remove this when this area has been developed.
        minHeight: '60px',
    },
    headerActions: {
        display: 'flex',
        flexGrow: 1,
        marginLeft: 'auto',
        justifyContent: 'flex-end',
        alignItems: 'flex-start',
        flexShrink: 0,
    },
    sendingEmailSettings: {
        display: 'flex',
        padding: '.5rem 1rem',
        minHeight: '5rem',
        width: '100%',
        backgroundColor: colors.green200,
        color: colors.black,
        justifyContent: 'space-between',
        alignItems: 'center',
        '& span': {
            fontWeight: 700,
        },
        animation: '.5s $fadeIn',
    },
    updateList: {
        paddingTop: '20px',
    },
    dialogButtonsContainer: {
        display: 'flex',
        width: '100%',
        justifyContent: 'space-around',
    },
    button: {
        ...baseButton,
        ...{
            cursor: 'pointer',
        },
    },
    modalButtons: {
        display: 'flex',
    },
    modalCheckbox: {
        display: 'flex',
        position: 'absolute',
        right: '20px',
        top: '20px',
    },
    numberOfUpdate: {
        display: 'flex',
        padding: '30px 0 90px',
        justifyContent: 'center',
        alignItems: 'center',
    },
    iconWrapper: {
        position: 'relative',
    },
    content: {
        display: '-webkit-box',
        WebkitLineClamp: '2',
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    deleteButtons: {
        top: '0',
        right: '0',
        display: 'flex',
        zIndex: '1',
        position: 'absolute',
        flexWrap: 'wrap',
        transition: 'all .3s ease-in',
        justifyContent: 'end',
        width: '100%',
        height: '100%',
        alignItems: 'center',
        background: 'rgba(255,255,255, .7)',
    },
    sortPlaceholder: {
        ...sortPlaceholder,
    },
    customIcon: {
        backgroundColor: colors.green500,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: colors.white,
        padding: '2px 5px',
        borderRadius: '4px',
    },
})

const gridTemplateColumns = '16% 23% 23% 21% 18%'
const gridTemplateColumnsUpdated = '16% 19% 21% 17% auto 18%'

const Updates: FC = () => {
    const [loading, setLoading] = useState(true)
    const classes = useStyles()
    const { setSort, sortState } = useSortMembersList()

    const [showDone, setShowDone] = useState(false)
    const [sortedUpdateDone] = useAtom(updatePageDoneAtom)
    const [sortedUpdateInProgress] = useAtom(updatePageInProgressAtom)
    const [updates, setAllUpdates] = useAtom(allUpdates)
    const [sortedUpdates] = useAtom(updatePageAtom)

    const [, _setAllUpdatesDone] = useAtom(allUpdatesDone)
    const [, _setUpdatesInProgress] = useAtom(allUpdatesInProgress)

    const init = (): void => {
        async function fetchData(): Promise<void> {
            setLoading(true)
            const today = new Date()

            // Set the target date to 30 days ago
            const targetDate = new Date()
            targetDate.setDate(today.getDate() - 30)
            const resZero = await getAllUpdates()
            const mapped = mapUpdates(resZero.data.OpdateringBatches)
            const filtered = mapped.filter((item) => {
                if (item.status === 'UnderOpdatering') {
                    return true
                }

                // Filter for items created within the last 30 days
                if (item.status === 'FaerdigBehandlet') {
                    return new Date(item.created) > targetDate
                }

                return false
            })

            setAllUpdates(
                filtered.filter((item) => item.type !== 'OpdaterTR_FTR')
            )
            setLoading(false)
        }

        fetchData()
    }

    useEffect(() => {
        if (!sortState) {
            setSort('created')
        }
        init()
    }, [])

    if (loading) return <Spinner />

    return (
        <div className={`${classes.container} page-up`}>
            <div className={`${classes.headerAreaTop} desktop-only`}>
                <div className={classes.headerActions}>
                    <HeaderLinks />
                </div>
            </div>
            <div className={classes.headerArea}>
                <div className="desktop-only">
                    <Typography tag="h1" size="XXXXL">
                        {showDone
                            ? L_HTML('MINEOPDATERINGER_UDFØRT_OVERSKRIFT')
                            : L_HTML('MINEOPDATERINGER_AFVENTER_OVERSKRIFT')}
                    </Typography>
                </div>
            </div>
            <div className={classes.headerArea}>
                <Typography tag="p" size="S" color={colors.gray900}>
                    {!showDone
                        ? L_HTML('MINEOPDATERINGER_AFVENTER_TEKST')
                        : L_HTML('MINEOPDATERINGER_UDFØRT_TEKST')}
                </Typography>
            </div>
            <div className={classes.headerArea}>
                <div>
                    <Typography size="XS">
                        {L_HTML(
                            'MINEOPDATERINGER_VISER-ANTAL_OPDATERINGER_TEXT',
                            {
                                antal: String(sortedUpdates.length),
                            }
                        )}
                    </Typography>
                </div>
            </div>

            <div className={`${classes.updateList}`}>
                {updates && updates.length ? (
                    <CardList>
                        <CardHeader
                            gridTemplateColumns={
                                showDone
                                    ? gridTemplateColumnsUpdated
                                    : gridTemplateColumns
                            }
                        >
                            <CardHeaderCell>
                                <SortButton
                                    onClick={() => setSort('created')}
                                    sort={sortState}
                                    name="created"
                                >
                                    {L(
                                        'MINEOPDATERINGER_INDSENDT_KOLONNE_OVERSKRIFT'
                                    )}
                                </SortButton>
                            </CardHeaderCell>
                            <CardHeaderCell>
                                <SortButton
                                    onClick={() => setSort('name')}
                                    sort={sortState}
                                    name="name"
                                >
                                    {L(
                                        'MINEOPDATERINGER_MEDLEM_KOLONNE_OVERSKRIFT'
                                    )}
                                </SortButton>
                            </CardHeaderCell>
                            <CardHeaderCell>
                                <SortButton
                                    onClick={() => setSort('updateType')}
                                    sort={sortState}
                                    name="updateType"
                                >
                                    {L(
                                        'MINEOPDATERINGER_TYPE_KOLONNE_OVERSKRIFT'
                                    )}
                                </SortButton>
                            </CardHeaderCell>
                            <CardHeaderCell>
                                <SortButton
                                    onClick={() => setSort('updateProfile')}
                                    sort={sortState}
                                    name="updateProfile"
                                >
                                    {L(
                                        'MINEOPDATERINGER_INDSENDT-AF_KOLONNE_OVERSKRIFT'
                                    )}
                                </SortButton>
                            </CardHeaderCell>
                            {showDone ? (
                                <CardHeaderCell>
                                    <SortButton
                                        onClick={() => setSort('updateUpdated')}
                                        sort={sortState}
                                        name="updateUpdated"
                                    >
                                        {L(
                                            'MINEOPDATERINGER_OPDATERET_KOLONNE_OVERSKRIFT'
                                        )}
                                    </SortButton>
                                </CardHeaderCell>
                            ) : null}
                            <CardHeaderCell>
                                <SortButton
                                    onClick={() => setSort('status')}
                                    sort={sortState}
                                    name="status"
                                >
                                    {L(
                                        'MINEOPDATERINGER_STATUS_KOLONNE_OVERSKRIFT'
                                    )}
                                </SortButton>
                            </CardHeaderCell>
                        </CardHeader>
                    </CardList>
                ) : null}
                {sortedUpdates.map((update) => (
                    <UpdateDetails
                        gridTemplateColumns={
                            showDone
                                ? gridTemplateColumnsUpdated
                                : gridTemplateColumns
                        }
                        showUpdatedColumn={showDone}
                        update={update}
                    />
                ))}
            </div>
            <div className={classes.numberOfUpdate}>
                <Typography size="XS">
                    {L_HTML('MINEOPDATERINGER_VISER-ANTAL_TEXT', {
                        antal: String(sortedUpdates.length),
                    })}
                </Typography>
            </div>
        </div>
    )
}

export { Updates }
